// Read-only detail view for a SOW. Mirrors DeliverableDetailExpand: <dl>
// label/value layout, status pill, clickable evidence links, an Edit
// button that the parent wires to swap the expanded row from view-mode
// to edit-mode (SowInlineExpand). Currency surfaces carry the
// ex-GST / no-GST / GST-status-not-recorded suffix.
//
// "Variations affecting this SOW" section: between the <dl> block and
// Evidence links. Lists every non-archived non-rejected variation that
// targets this SOW (proposed + approved + non-financial). Each row
// shows number + name, status pill, signed contribution per the
// multi-target three-case rule, and effective date. Click on a row
// fires `onNavigateToVariation(id)` so the parent can scroll to and
// expand the variation in its own table. Section is omitted entirely
// when no qualifying variations exist.

import { Pencil, ExternalLink, Plus } from "lucide-react";
import { safeExternalHref } from "../../../../utils/evidenceLinks";
import Button from "../../../common/Button";
import SowStatusPill from "./SowStatusPill";
import VariationStatusPill from "./VariationStatusPill";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import { deriveDeliverableStatus } from "../deliverable/deriveDeliverableStatus";
import { deriveMilestoneLifecycleStatus } from "../../../../utils/deriveMilestoneStatus";

import { formatSowCurrency } from "./formatSowCurrency";
import {
  approvedSowVariations,
  classifySowVariation,
  currentSowValue,
  sowVariations,
} from "./contractValueDerivation";
import { gstSuffix } from "../../../../utils/gstSuffix";
import type {
  Deliverable,
  Milestone,
  Sow,
  Variation,
} from "../../../../types";

const UNICODE_MINUS = "−";

interface SowDetailExpandProps {
  item: Sow;
  gstApplies: boolean | undefined;
  /** Variations on the parent contract — used to derive the current
   *  value annotation when approved sow-scope variations affect this SOW. */
  variations?: Variation[];
  /** Deliverables on the parent contract — drives the "Deliverables
   *  linked to this SOW" section. Filtered to non-archived items
   *  whose `sowId === item.id`. Section omitted when no callbacks
   *  AND no linked items. */
  deliverables?: Deliverable[];
  /** Milestones on the parent contract — drives the "Milestones
   *  linked to this SOW" section. Same filter rules. */
  milestones?: Milestone[];
  onEdit?: () => void;
  /** Open the editor-gated `Correct value` modal. Parent wires the
   *  mutation. Passing undefined hides the trigger entirely. */
  onCorrectValue?: () => void;
  /** Open the editor-gated `Correct status` modal. */
  onCorrectStatus?: () => void;
  /** Click handler for the variation rows in the "Variations affecting
   *  this SOW" section. Parent wires this to scroll-to-and-expand the
   *  variation in its own register. */
  onNavigateToVariation?: (variationId: string) => void;
  /** Click handler for the deliverable rows in the "Deliverables
   *  linked to this SOW" section — parent navigates to the
   *  deliverable tab with the row focused. */
  onNavigateToDeliverable?: (deliverableId: string) => void;
  /** Click handler for the milestone rows in the "Milestones
   *  linked to this SOW" section. */
  onNavigateToMilestone?: (milestoneId: string) => void;
  /** Triggered by the section's "Add deliverable" button. Parent
   *  navigates to the deliverable tab and pre-fills the create form
   *  with `sowId = item.id`. Hidden when undefined. */
  onAddDeliverableToSow?: (sowId: string) => void;
  /** Triggered by the section's "Add milestone" button. Mirror. */
  onAddMilestoneToSow?: (sowId: string) => void;
}

function formatSignedAud(amount: number): string {
  const abs = Math.abs(amount);
  const formatted = `$${abs.toLocaleString("en-AU", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
  if (amount < 0) return `${UNICODE_MINUS}${formatted}`;
  if (amount > 0) return `+${formatted}`;
  return formatted;
}

function fmtDate(v?: string): string {
  if (!v) return "—";
  try {
    return new Date(`${v}T00:00:00`).toLocaleDateString("en-AU", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  } catch {
    return v;
  }
}

function DL({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <dt className="text-caption text-slate-500 dark:text-slate-400">
        {label}
      </dt>
      <dd className="mt-1 text-body text-slate-900 dark:text-slate-100">
        {children}
      </dd>
    </div>
  );
}

export default function SowDetailExpand({
  item,
  gstApplies,
  variations = [],
  deliverables = [],
  milestones = [],
  onEdit,
  onCorrectValue,
  onCorrectStatus,
  onNavigateToVariation,
  onNavigateToDeliverable,
  onNavigateToMilestone,
  onAddDeliverableToSow,
  onAddMilestoneToSow,
}: SowDetailExpandProps) {
  const original = formatSowCurrency(item.originalValue, gstApplies);
  // Approved-only list drives the inline `+ $X from VAR-N` annotation
  // on the Current value row; the broader Variations-affecting-this-SOW
  // section below uses the proposed-and-approved list.
  const approvedVariationsForSow = approvedSowVariations(item.id, variations);
  const variationsForSow = sowVariations(item.id, variations);
  const current = formatSowCurrency(
    currentSowValue(item, variations),
    gstApplies,
  );
  const period = `${fmtDate(item.startDate)} – ${fmtDate(item.endDate)}`;
  const basis = gstSuffix(gstApplies);

  // Linked items — non-archived only. Operational view; archived rows
  // belong to the deliverable/milestone tabs' include-archived chip,
  // not this read-only summary panel.
  const linkedDeliverables = deliverables.filter(
    (d) => d.sowId === item.id && !d.archivedAt,
  );
  const linkedMilestones = milestones.filter(
    (m) => m.sowId === item.id && !m.archivedAt,
  );

  // Add buttons hidden for archived/cancelled SOWs — restoring those
  // states is not a creation flow.
  const canAddLinkedItems =
    !item.archivedAt && item.status !== "cancelled";

  return (
    <section
      data-testid="sow-detail-expand"
      className="space-y-5 p-4"
      aria-label={`Details for ${item.name}`}
    >
      <header className="flex items-start justify-between gap-3">
        <div className="space-y-1">
          <h3 className="text-h3 text-slate-900 dark:text-slate-100">
            {item.name}
          </h3>
          <div className="flex flex-wrap items-center gap-2">
            <SowStatusPill status={item.status} />
            <span
              className="font-mono text-caption text-slate-500 dark:text-slate-400"
              data-testid="sow-detail-number"
            >
              {item.number}
            </span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {onCorrectStatus && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onCorrectStatus}
              data-testid="sow-detail-correct-status"
            >
              Correct status
            </Button>
          )}
          {onCorrectValue && (
            <Button
              variant="secondary"
              size="sm"
              onClick={onCorrectValue}
              data-testid="sow-detail-correct-value"
            >
              Correct value
            </Button>
          )}
          {onEdit && (
            <Button
              variant="secondary"
              size="sm"
              icon={<Pencil className="h-4 w-4" />}
              onClick={onEdit}
              data-testid="sow-detail-edit"
            >
              Edit
            </Button>
          )}
        </div>
      </header>

      <dl className="flex flex-wrap gap-x-10 gap-y-4">
        <DL label="Period">{period}</DL>
        <DL label="Value">
          <span className="tabular-nums" data-testid="sow-detail-original-value">
            {original.value}
          </span>{" "}
          <span className="text-caption text-slate-500 dark:text-slate-400">
            {original.suffix}
          </span>
        </DL>
        {approvedVariationsForSow.length > 0 && (
          <DL label="Current value">
            <span className="tabular-nums" data-testid="sow-detail-current-value">
              {current.value}
            </span>{" "}
            <span className="text-caption text-slate-500 dark:text-slate-400">
              {current.suffix}
            </span>
            <p
              data-testid="sow-detail-variations-annotation"
              className="text-caption text-slate-500 dark:text-slate-400 mt-1"
            >
              {original.value}
              {approvedVariationsForSow.map((v) => {
                const sign = (v.valueImpact ?? 0) >= 0 ? "+" : "−";
                const abs = Math.abs(v.valueImpact ?? 0);
                const formatted = `$${abs.toLocaleString("en-AU", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}`;
                return ` ${sign} ${formatted} from ${v.number}`;
              })}
            </p>
          </DL>
        )}
      </dl>
      {item.description && (
        <DL label="Description">
          <span className="whitespace-pre-wrap">{item.description}</span>
        </DL>
      )}

      {variationsForSow.length > 0 && (
        <div data-testid="sow-detail-variations-list">
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Variations affecting this SOW
          </h4>
          <ul className="mt-2 divide-y divide-slate-200 dark:divide-slate-700">
            {variationsForSow.map((v) => {
              const cls = classifySowVariation(item.id, v);
              const isNonFinancial = v.valueImpact === null;
              const rowTestId = `sow-detail-variation-${v.id}`;
              return (
                <li key={v.id}>
                  <button
                    type="button"
                    onClick={() => onNavigateToVariation?.(v.id)}
                    disabled={!onNavigateToVariation}
                    data-testid={rowTestId}
                    aria-label={`View variation ${v.number}`}
                    className="flex w-full flex-wrap items-center gap-x-4 gap-y-1 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm disabled:cursor-default disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                  >
                    <div className="flex flex-col">
                      <span className="text-body-sm">
                        <span className="font-mono text-brand-600 dark:text-brand-400">
                          {v.number}
                        </span>
                        <span className="text-slate-500 dark:text-slate-400">
                          {" · "}
                        </span>
                        <span className="text-slate-900 dark:text-slate-100">
                          {v.name}
                        </span>
                      </span>
                      {cls.mode === "unallocated-multi" &&
                        cls.totalImpact !== null && (
                          <span
                            data-testid={`${rowTestId}-unallocated-note`}
                            className="text-caption text-slate-500 dark:text-slate-400"
                          >
                            unallocated · total{" "}
                            {formatSignedAud(cls.totalImpact)} {basis}{" "}
                            across {cls.targetCount} targets
                          </span>
                        )}
                    </div>
                    <div className="inline-flex items-center gap-3">
                      <VariationStatusPill status={v.status} />
                      <span
                        className="tabular-nums text-body-sm text-slate-900 dark:text-slate-100"
                        data-testid={`${rowTestId}-contribution`}
                      >
                        {isNonFinancial ? (
                          <span className="text-slate-400 dark:text-slate-500">
                            {"—"} non-financial
                          </span>
                        ) : cls.mode === "unallocated-multi" ? (
                          // Per-SOW attribution undefined; show an em-dash
                          // here and let the caption above carry the total.
                          <span className="text-slate-400 dark:text-slate-500">
                            {"—"}
                          </span>
                        ) : (
                          <>
                            {formatSignedAud(cls.contribution)}{" "}
                            <span className="text-caption font-normal text-slate-500 dark:text-slate-400">
                              {basis}
                            </span>
                          </>
                        )}
                      </span>
                      <span className="text-caption text-slate-500 dark:text-slate-400">
                        {fmtDate(v.effectiveDate)}
                      </span>
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {(linkedDeliverables.length > 0 ||
        (canAddLinkedItems && onAddDeliverableToSow)) && (
        <div data-testid="sow-detail-deliverables-section">
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Deliverables linked to this SOW
          </h4>
          {linkedDeliverables.length > 0 && (
            <ul className="mt-2 divide-y divide-slate-200 dark:divide-slate-700">
              {linkedDeliverables.map((d) => {
                const derived = deriveDeliverableStatus(
                  d.status,
                  d.dueDate,
                  new Date(),
                  d.submittedAt,
                );
                const rowTestId = `sow-detail-deliverable-${d.id}`;
                return (
                  <li key={d.id}>
                    <button
                      type="button"
                      onClick={() => onNavigateToDeliverable?.(d.id)}
                      disabled={!onNavigateToDeliverable}
                      data-testid={rowTestId}
                      aria-label={`View deliverable ${d.name}`}
                      className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm disabled:cursor-default disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                    >
                      <span className="text-body-sm text-slate-900 dark:text-slate-100">
                        {d.name}
                      </span>
                      <TrackingStatusBadge
                        kind="deliverable"
                        status={derived}
                      />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {canAddLinkedItems && onAddDeliverableToSow && (
            <div className="mt-3">
              <Button
                variant="secondary"
                size="sm"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => onAddDeliverableToSow(item.id)}
                data-testid="sow-detail-add-deliverable"
              >
                Add deliverable to this SOW
              </Button>
            </div>
          )}
        </div>
      )}

      {(linkedMilestones.length > 0 ||
        (canAddLinkedItems && onAddMilestoneToSow)) && (
        <div data-testid="sow-detail-milestones-section">
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Milestones linked to this SOW
          </h4>
          {linkedMilestones.length > 0 && (
            <ul className="mt-2 divide-y divide-slate-200 dark:divide-slate-700">
              {linkedMilestones.map((m) => {
                const display = deriveMilestoneLifecycleStatus(m);
                const rowTestId = `sow-detail-milestone-${m.id}`;
                return (
                  <li key={m.id}>
                    <button
                      type="button"
                      onClick={() => onNavigateToMilestone?.(m.id)}
                      disabled={!onNavigateToMilestone}
                      data-testid={rowTestId}
                      aria-label={`View milestone ${m.name}`}
                      className="flex w-full flex-wrap items-center justify-between gap-x-4 gap-y-1 py-2 text-left hover:bg-slate-50 dark:hover:bg-slate-800/60 focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 rounded-sm disabled:cursor-default disabled:hover:bg-transparent dark:disabled:hover:bg-transparent"
                    >
                      <span className="text-body-sm text-slate-900 dark:text-slate-100">
                        {m.name}
                      </span>
                      <span className="inline-flex items-center gap-3">
                        <TrackingStatusBadge
                          kind="milestone"
                          status={display}
                        />
                        <span className="text-caption text-slate-500 dark:text-slate-400">
                          {fmtDate(m.plannedDate)}
                        </span>
                      </span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
          {canAddLinkedItems && onAddMilestoneToSow && (
            <div className="mt-3">
              <Button
                variant="secondary"
                size="sm"
                icon={<Plus className="h-4 w-4" />}
                onClick={() => onAddMilestoneToSow(item.id)}
                data-testid="sow-detail-add-milestone"
              >
                Add milestone to this SOW
              </Button>
            </div>
          )}
        </div>
      )}

      {item.evidenceLinks && item.evidenceLinks.length > 0 && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Evidence links
          </h4>
          <ul className="mt-2 space-y-1">
            {item.evidenceLinks.map((link, i) => {
              const href = safeExternalHref(link.url);
              const text = link.label || link.url;
              return (
                <li key={i}>
                  {href ? (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-body text-brand-600 hover:text-brand-500 dark:text-brand-400 hover:underline"
                    >
                      {text}
                      <ExternalLink className="h-3.5 w-3.5" />
                    </a>
                  ) : (
                    <span className="text-body text-slate-700 dark:text-slate-300">
                      {text}
                    </span>
                  )}
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {item.notes && (
        <div>
          <h4 className="text-caption text-slate-500 dark:text-slate-400">
            Notes
          </h4>
          <p className="mt-1 text-body whitespace-pre-wrap text-slate-900 dark:text-slate-100">
            {item.notes}
          </p>
        </div>
      )}
    </section>
  );
}
